import { faXmark } from "@fortawesome/pro-solid-svg-icons/faXmark";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Button, Divider, TextInput } from "@mantine/core";
import { useDebouncedCallback } from "@mantine/hooks";
import {
  type ChangeEvent,
  type PropsWithChildren,
  useCallback,
  useState,
} from "react";
import { twMerge } from "tailwind-merge";

import { useUserContext } from "@/context/user.context";
import { useTrackAnalytics } from "@/hooks/analytics.hooks";
import type {
  FilterParamsHandle,
  ParamsConstraint,
} from "@/hooks/use-filter-params";
import type { BaseTableQueryParams } from "@/types/api";
import { isEmptyAfterTrim } from "@/utils/string";
import { LAYOUT_DIMENSIONS } from "@/utils/ui";

export const FILTER_MENU_TEST_IDS = {
  container: "filter-menu-container",
};

type SearchableParams = ParamsConstraint & Pick<BaseTableQueryParams, "search">;

export type FilterMenuProps<T extends SearchableParams> = PropsWithChildren<{
  clearAllFilters(): void;
  filters: FilterParamsHandle<T>;
  hasClearableFilters: boolean;
  placeHolder?: string;
  rowCount: number;
  searchCountName: string;
}>;

export const FilterMenu = <T extends SearchableParams>({
  children,
  clearAllFilters,
  filters,
  hasClearableFilters,
  placeHolder = "Search...",
  rowCount,
  searchCountName,
}: FilterMenuProps<T>) => {
  const track = useTrackAnalytics();
  const { user } = useUserContext();

  const searchValue = filters.getWithDefault(
    "search" as keyof T,
    "" as T[keyof T],
  ) as string;
  const [localSearchState, setLocalSearchState] = useState(() => ({
    searchValue,
    value: searchValue,
  }));
  const localSearchValue =
    localSearchState.searchValue === searchValue
      ? localSearchState.value
      : searchValue;
  const setLocalSearchValue = useCallback(
    (value: string) => {
      setLocalSearchState({ searchValue, value });
    },
    [searchValue],
  );

  const handleSearch = (value: string) => {
    if (!isEmptyAfterTrim(value)) {
      filters.set("search" as keyof T, value as T[keyof T]);
    } else {
      filters.delete("search" as keyof T);
    }
  };
  const debouncedOnSearch = useDebouncedCallback(handleSearch, 150);

  const trackSearchUsed = useDebouncedCallback(
    () => track("Table Searched"),
    1000,
  );

  const handleSearchChange = (event: ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setLocalSearchValue(value);
    if (value) {
      trackSearchUsed();
    }
    debouncedOnSearch(value);
  };

  return (
    <div
      className={twMerge(
        "border-gray-200",
        "border-r",
        "flex",
        "flex-col",
        "overflow-auto",
        "p-4",
      )}
      style={{
        height: user?.isInternal
          ? LAYOUT_DIMENSIONS.internalUserTableViewContentHeight
          : LAYOUT_DIMENSIONS.tableViewContentHeight,
        width: LAYOUT_DIMENSIONS.FILTER_MENU_WIDTH_PX,
      }}
      data-testid={FILTER_MENU_TEST_IDS.container}
    >
      <div className="font-semibold text-sm">
        Showing {rowCount} {searchCountName}
      </div>
      <div className="flex flex-col">
        <Button
          variant="transparent"
          className="tracking-wider"
          size="compact-xs"
          onClick={clearAllFilters}
          fullWidth
          styles={{
            inner: {
              fontSize: "0.766rem",
              fontWeight: "600",
              justifyContent: "flex-start",
            },
            root: {
              padding: 0,
              visibility: hasClearableFilters ? "visible" : "hidden",
            },
          }}
        >
          CLEAR ALL FILTERS
          <FontAwesomeIcon className="ml-1.5" icon={faXmark} />
        </Button>
      </div>
      <Divider mb="sm" />
      <TextInput
        placeholder={placeHolder}
        onChange={handleSearchChange}
        value={localSearchValue}
      />
      <div className="flex flex-col">{children}</div>
    </div>
  );
};
